import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  MUSIC_VIDEO_CUT_INS,
  MUSIC_VIDEO_MOVES,
  type MusicVideoMove,
  type MusicVideoSpeed,
} from 'fym-shared/src/music24/musicVideoLook';
import type { MusicVideoClip } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { updateClip, type ClipPatch } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { cutInMessages, gradeMessages, moveMessages, panelMessages } from '../messages/panelMessages';
import { Segmented } from '../panels/Segmented';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The speed, the move, the cut-in and the colour of a clip (design b1, c3). */
const ClipControlsComponent: React.FC<{ clip: MusicVideoClip }> = ({ clip }) => {
  const intl = useIntl();
  const { change, doc, readOnly } = useMusicVideoEditor();
  const limitsFor = useClipLimits();
  // A faster speed that runs past the end of the source repeats the clip (`loopClip`).
  const patch = (next: ClipPatch) => change((current) => updateClip(current, clip.id, next, limitsFor()));
  // "Follow the face" is for own clips (design c3 note); a bank clip keeps it only if it has it.
  const moves = useMemo(
    () =>
      MUSIC_VIDEO_MOVES.filter((move) => move !== 'followFace' || clip.source.kind === 'footage' || clip.move === move),
    [clip.move, clip.source.kind]
  );
  const speeds: ReadonlyArray<{ value: MusicVideoSpeed; label: string }> = [
    { value: 0.5, label: intl.formatMessage(panelMessages.speedHalf) },
    { value: 1, label: intl.formatMessage(panelMessages.speedOne) },
    { value: 2, label: intl.formatMessage(panelMessages.speedTwo) },
  ];

  return (
    <div className={styles.inspectorGrid}>
      <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.speed)}</span>
      <Segmented
        label={intl.formatMessage(panelMessages.speed)}
        options={speeds}
        value={clip.speed}
        disabled={readOnly}
        onChange={(speed) => patch({ speed })}
      />
      <label className={styles.rowLabel} htmlFor={`clip-move-${clip.id}`}>
        {intl.formatMessage(panelMessages.move)}
      </label>
      <select
        id={`clip-move-${clip.id}`}
        className={styles.select}
        value={clip.move}
        disabled={readOnly}
        onChange={(event) => patch({ move: event.target.value as MusicVideoMove })}
      >
        {moves.map((move) => (
          <option key={move} value={move}>
            {intl.formatMessage(moveMessages[move])}
          </option>
        ))}
      </select>
      <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.cutIn)}</span>
      <Segmented
        label={intl.formatMessage(panelMessages.cutIn)}
        options={MUSIC_VIDEO_CUT_INS.map((cutIn) => ({
          value: cutIn,
          label: intl.formatMessage(cutInMessages[cutIn]),
        }))}
        value={clip.cutIn}
        disabled={readOnly}
        onChange={(cutIn) => patch({ cutIn })}
      />
      <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.colourOfVideo)}</span>
      <span className={styles.field}>
        {intl.formatMessage(panelMessages.colourValue, { grade: intl.formatMessage(gradeMessages[doc.look.grade]) })}
      </span>
    </div>
  );
};

export const ClipControls = TypedMemo(ClipControlsComponent);
